import { Injectable } from '@angular/core';
import { MakeModel } from '@app/_models/makeModel';

export interface ModelMatchResult {
  make: string;
  model: string;
  updatedModelArray: string[];
  matchedMakeIndex: number;
}

@Injectable({
  providedIn: 'root'
})
export class CommonFunctionService {

  constructor() { }

  /**
   * Calculate Levenshtein distance between two strings
   * Lower distance means more similar strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = [];
    const len1 = str1.length;
    const len2 = str2.length;

    if (len1 === 0) return len2;
    if (len2 === 0) return len1;

    // Initialize matrix
    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }

    // Fill matrix
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,     // deletion
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j - 1] + 1   // substitution
          );
        }
      }
    }

    return matrix[len1][len2];
  }

  /**
   * Calculate similarity score between two strings (0-1, where 1 is identical)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const normalized1 = str1.toLowerCase().trim();
    const normalized2 = str2.toLowerCase().trim();

    // Exact match
    if (normalized1 === normalized2) {
      return 1.0;
    }

    // Check if one contains the other
    if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
      return 0.8;
    }

    // Use Levenshtein distance for similarity
    const maxLen = Math.max(normalized1.length, normalized2.length);
    if (maxLen === 0) return 1.0;

    const distance = this.levenshteinDistance(normalized1, normalized2);
    return 1 - (distance / maxLen);
  }

  /**
   * Find closest model match in makeModel array using fuzzy matching
   * @param modelName The model name to search for
   * @param makeModelArray The array of MakeModel objects to search in
   * @returns ModelMatchResult with the closest match, or null if no good match found
   */
  findClosestModelMatch(modelName: string, makeModelArray: MakeModel[]): ModelMatchResult | null {
    if (!modelName || !makeModelArray || makeModelArray.length === 0) {
      return null;
    }

    const normalizedSearchModel = modelName.trim();
    let bestMatch: {
      make: string;
      model: string;
      similarity: number;
      makeIndex: number;
      modelIndex: number;
    } | null = null;

    // Search through all makes and their models
    for (let makeIndex = 0; makeIndex < makeModelArray.length; makeIndex++) {
      const makeModel = makeModelArray[makeIndex];
      const models = makeModel.model || [];

      for (let modelIndex = 0; modelIndex < models.length; modelIndex++) {
        const existingModel = models[modelIndex];
        const similarity = this.calculateSimilarity(normalizedSearchModel, existingModel);

        // Keep track of best match
        if (!bestMatch || similarity > bestMatch.similarity) {
          bestMatch = {
            make: makeModel.make,
            model: existingModel,
            similarity: similarity,
            makeIndex: makeIndex,
            modelIndex: modelIndex
          };
        }
      }
    }

    // If we found a good match (similarity >= 0.6), return the closest matching make
    // Otherwise, find the make with most similar name to the model
    // Only proceed if similarity is at least 60%
    if (bestMatch && bestMatch.similarity >= 0.6) {
      const matchedMakeModel = makeModelArray[bestMatch.makeIndex];
      const updatedModels = [...matchedMakeModel.model];
      
      // Add the new model if it doesn't already exist (case-insensitive)
      const modelExists = updatedModels.some(
        m => m.toLowerCase().trim() === normalizedSearchModel.toLowerCase()
      );
      
      if (!modelExists) {
        updatedModels.push(normalizedSearchModel);
      }

      return {
        make: matchedMakeModel.make,
        model: normalizedSearchModel,
        updatedModelArray: updatedModels,
        matchedMakeIndex: bestMatch.makeIndex
      };
    }

    // No good match found - find make with similar name to model
    // This handles cases where model name might actually be a make name
    let bestMakeMatch: {
      make: string;
      similarity: number;
      makeIndex: number;
    } | null = null;

    for (let makeIndex = 0; makeIndex < makeModelArray.length; makeIndex++) {
      const makeModel = makeModelArray[makeIndex];
      const similarity = this.calculateSimilarity(normalizedSearchModel, makeModel.make);

      if (!bestMakeMatch || similarity > bestMakeMatch.similarity) {
        bestMakeMatch = {
          make: makeModel.make,
          similarity: similarity,
          makeIndex: makeIndex
        };
      }
    }

    // If we found a reasonable make match with at least 60% similarity, add model to that make
    if (bestMakeMatch && bestMakeMatch.similarity >= 0.6) {
      const matchedMakeModel = makeModelArray[bestMakeMatch.makeIndex];
      const updatedModels = [...matchedMakeModel.model];
      
      // Add the new model if it doesn't already exist
      const modelExists = updatedModels.some(
        m => m.toLowerCase().trim() === normalizedSearchModel.toLowerCase()
      );
      
      if (!modelExists) {
        updatedModels.push(normalizedSearchModel);
      }

      return {
        make: matchedMakeModel.make,
        model: normalizedSearchModel,
        updatedModelArray: updatedModels,
        matchedMakeIndex: bestMakeMatch.makeIndex
      };
    }

    // Don't add to first make as fallback - only if similarity >= 60%
    // Return null if no good match found (similarity < 60%)

    return null;
  }
}
